<template>
  <div class="recent-updates">
    <div class="header">
      <div class="left" @click="$emit('close')">
        <van-icon name="arrow-left" size="20" color="#333" />
      </div>
      <div class="title">{{ $t('recent_updates') }}</div>
      <div class="right"></div>
    </div>

    <div ref="content" class="content">
      <!-- Top Summary Section with Highlight Style -->
      <div class="highlight-box">
        <div class="box-header">
          <div class="box-title">
            <van-icon name="chart-trending-o" color="#aa341b" size="18" style="margin-right: 6px;" />
            <span>{{ $t('smart_summary') }}</span>
          </div>
          <div v-if="!isLoading || summaryBullets.length" class="refresh-btn" @click="loadRecentUpdates(true)">
            <van-icon name="replay" size="16" />
            <span>{{ $t('refresh') }}</span>
          </div>
        </div>

        <div v-if="isLoading && !summaryBullets.length" class="ai-loading-container">
          <div class="ai-bubble">
            <div class="typing-indicator">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
          </div>
        </div>

        <div v-else class="summary-list">
          <div v-for="(item, index) in summaryBullets" :key="index" class="summary-item">
            <span class="bullet-point"></span>
            <span class="text" v-html="formatMessage(item)"></span>
          </div>
          <div v-if="!summaryBullets.length" class="empty">{{ $t('no_recent_updates') }}</div>
        </div>
      </div>

      <!-- Detail Section -->
      <div class="section-container">
        <div class="section-title">{{ $t('detailed_analysis') }}</div>
        <div v-if="isLoading && !detailBlocks.length" class="ai-loading-container">
          <div class="ai-bubble">
            <div class="typing-indicator">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
          </div>
        </div>
        <div v-else class="detail-content">
          <template v-if="detailBlocks.length">
            <div v-for="(block, index) in detailBlocks" :key="index" class="detail-block">
              <div v-if="block.type === 'text'" class="detail-text" v-html="formatMessage(block.text)"></div>
              <div v-else-if="block.type === 'customers'" class="customer-update-list">
                <article v-for="(customer, i) in block.items" :key="`${customer.name}-${i}`" class="customer-update-card">
                  <div class="customer-update-head">
                    <div class="customer-update-avatar">{{ getCustomerInitial(customer.name) }}</div>
                    <div class="customer-update-identity">
                      <strong v-html="formatMessage(customer.name)"></strong>
                      <span v-if="customer.institution" v-html="formatMessage(customer.institution)"></span>
                    </div>
                    <span v-if="customer.activityType" class="customer-update-type" v-html="formatMessage(customer.activityType)"></span>
                  </div>
                  <div v-if="customer.summary" class="customer-update-summary" v-html="formatMessage(customer.summary)"></div>
                  <div v-if="customer.dateOrSource" class="customer-update-meta">
                    <van-icon name="clock-o" size="12" />
                    <span v-html="formatMessage(customer.dateOrSource)"></span>
                  </div>
                </article>
              </div>
              <ul v-else class="detail-list">
                <li v-for="(item, i) in block.items" :key="i">
                  <span class="list-dot"></span>
                  <span v-html="formatMessage(item)"></span>
                </li>
              </ul>
            </div>
          </template>
          <div v-else class="empty-text">{{ $t('no_detail_content') }}</div>
        </div>
      </div>

      <!-- Suggestion Section -->
      <div v-if="!isLoading && nextActionSuggestions.length" class="section-container">
        <div class="section-title">{{ $t('next_action_suggestions') }}</div>
        <div class="detail-content">
          <div class="detail-block">
            <ul class="detail-list">
              <li v-for="(item, i) in nextActionSuggestions" :key="i">
                <span class="list-dot" style="background-color: #aa341b;"></span>
                <span v-html="formatMessage(item)"></span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Q&A Section -->
      <div v-if="!isLoading" class="section-container">
        <div class="section-title">{{ $t('further_understanding') }}</div>
        <div class="follow-up-list">
          <div
            v-for="(q, qIndex) in followUpQuestions"
            :key="qIndex"
            class="follow-up-chip"
            @click="askFollowUp(q)"
          >
            <span class="follow-up-label">{{ q }}</span>
            <van-icon name="arrow" class="follow-up-arrow" size="12" />
          </div>

          <div class="custom-input-wrapper">
            <span class="custom-ai-icon">
              <van-icon name="edit" size="14" />
            </span>
            <input
              v-model="customQuestion"
              class="custom-question-input"
              :placeholder="$t('ask_something_else')"
              @keyup.enter="handleCustomQuestion"
            />
            <div v-if="customQuestion" class="send-btn" @click="handleCustomQuestion">
              <van-icon name="guide-o" size="18" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from 'vant';
import { streamMyHCPUpdates, saveUserRecentHcpUpdateSummary } from '@/api/ai';
import { formatMessage } from '@/utils/index';
import moment from 'moment';

export default {
  name: 'RecentUpdates',
  components: {
    [Icon.name]: Icon
  },
  props: {
    userId: {
      type: [String, Number],
      required: true
    },
    roleId: {
      type: [String, Number],
      default: ''
    },
    cachedText: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isLoading: false,
      rawText: '',
      summaryBullets: [],
      detailBlocks: [],
      nextActionSuggestions: [],
      customQuestion: '',
      inputWidth: 128,
      animationFrameId: null
    };
  },
  computed: {
    followUpQuestions() {
      return [
        this.$t('follow_up_q1'),
        this.$t('follow_up_q2'),
        this.$t('follow_up_q3')
      ];
    }
  },
  watch: {
    customQuestion() {
      this.updateInputWidth();
    }
  },
  mounted() {
    if (this.cachedText) {
      this.animateText(this.cachedText);
    } else {
      this.loadRecentUpdates();
    }
    this.updateInputWidth();
  },
  beforeDestroy() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  },
  methods: {
    updateInputWidth() {
      this.$nextTick(() => {
        if (this.$refs.textMeasurer) {
          this.inputWidth = this.$refs.textMeasurer.offsetWidth + 47;
        }
      });
    },
    isListLine(line) {
      return /^[-•*]\s*/.test(line) || /^\d+\.\s+/.test(line);
    },
    stripListMarker(line) {
      const withoutBullet = line.replace(/^[-•*]\s*/, '').trim();
      return withoutBullet.replace(/^\d+\.\s+/, '').trim();
    },
    parseCustomerDetail(line) {
      const content = this.stripListMarker(line);
      const fields = content.split(/\s*[｜|]\s*/).map(field => field.trim()).filter(Boolean);
      if (fields.length < 5) return null;

      return {
        name: fields[0],
        institution: fields[1],
        activityType: fields[2],
        summary: fields.slice(3, -1).join('｜'),
        dateOrSource: fields[fields.length - 1]
      };
    },
    getCustomerInitial(name) {
      const plainName = String(name || '')
        .replace(/[*_`~#]/g, '')
        .trim();
      return plainName.slice(0, 1) || '客';
    },
    normalizeText(text) {
      return text.replace(/^```[a-zA-Z]*\s*/, '').replace(/\s*```$/, '').trim();
    },
    parseRecentUpdates(text) {
      if (!text) return { summaryBullets: [], detailBlocks: [], nextActionSuggestions: [] };

      const cleanText = this.normalizeText(text);
      const summaryHeaderRegex = /【\s*(?:近期动态汇总|Recent (?:Dynamic(?:s)?|Activity) Summary)\s*】/i;
      const detailHeaderRegex = /【\s*(?:近期动态详情|Recent (?:Dynamic(?:s)?|Activity) Detail(?:s)?)\s*】/i;
      const suggestionHeaderRegex = /【\s*(?:下一步行动建议|Next (?:Action Suggestions|Step Recommendations))\s*】/i;

      // Extract parts using split
      // Assume order: Summary -> Detail -> Suggestions
      // But we should be robust.
      // Easiest is to find indexes.

      let summaryPart = '';
      let detailPart = '';
      let suggestionPart = '';

      const summaryMatch = cleanText.match(summaryHeaderRegex);
      const detailMatch = cleanText.match(detailHeaderRegex);
      const suggestionMatch = cleanText.match(suggestionHeaderRegex);

      const indices = [
        { name: 'summary', index: summaryMatch ? summaryMatch.index : -1, length: summaryMatch ? summaryMatch[0].length : 0 },
        { name: 'detail', index: detailMatch ? detailMatch.index : -1, length: detailMatch ? detailMatch[0].length : 0 },
        { name: 'suggestion', index: suggestionMatch ? suggestionMatch.index : -1, length: suggestionMatch ? suggestionMatch[0].length : 0 }
      ].sort((a, b) => a.index - b.index);

      for (let i = 0; i < indices.length; i++) {
        const current = indices[i];
        if (current.index === -1) continue;

        const next = indices[i + 1];
        const end = next && next.index !== -1 ? next.index : cleanText.length;
        const content = cleanText.substring(current.index + current.length, end).trim();

        if (current.name === 'summary') summaryPart = content;
        if (current.name === 'detail') detailPart = content;
        if (current.name === 'suggestion') suggestionPart = content;
      }

      // Parse Summary
      const summaryLines = summaryPart
        .split('\n')
        .map(line => line.trim())
        .filter(line => line);

      const summaryBullets = summaryLines
        .map(line => this.stripListMarker(line))
        .filter(line => line);

      // Parse Detail
      const detailBlocks = [];
      const detailSections = detailPart.split(/\n\s*\n/).map(part => part.trim()).filter(Boolean);

      detailSections.forEach(section => {
        const lines = section.split('\n').map(line => line.trim()).filter(Boolean);
        const bulletLines = lines.filter(line => this.isListLine(line));
        if (bulletLines.length === lines.length && bulletLines.length > 0) {
          const customerItems = bulletLines.map(line => this.parseCustomerDetail(line));
          if (customerItems.every(Boolean)) {
            detailBlocks.push({
              type: 'customers',
              items: customerItems
            });
          } else {
            detailBlocks.push({
              type: 'list',
              items: bulletLines.map(line => this.stripListMarker(line)).filter(Boolean)
            });
          }
        } else {
          detailBlocks.push({
            type: 'text',
            text: lines.join('\n')
          });
        }
      });

      // Parse Suggestions
      const suggestionLines = suggestionPart
        .split('\n')
        .map(line => line.trim())
        .filter(line => line);

      const nextActionSuggestions = suggestionLines
        .map(line => this.stripListMarker(line))
        .filter(line => line);

      return { summaryBullets, detailBlocks, nextActionSuggestions };
    },
    formatMessage,
    applyParsedText(text) {
      const parsed = this.parseRecentUpdates(text);
      this.summaryBullets = parsed.summaryBullets;
      this.detailBlocks = parsed.detailBlocks;
      this.nextActionSuggestions = parsed.nextActionSuggestions || [];
      this.$emit('summary-updated', {
        summaryBullets: parsed.summaryBullets,
        rawText: text
      });
    },
    scrollContentToBottom() {
      this.$nextTick(() => {
        const content = this.$refs.content;
        if (content) {
          content.scrollTop = content.scrollHeight;
        }
      });
    },
    animateText(fullText) {
      if (!fullText) return;
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }

      this.isLoading = true;
      this.rawText = '';
      let index = 0;
      const step = Math.min(28, Math.max(10, Math.ceil(fullText.length / 70)));

      const animate = () => {
        // If component destroyed, stop animation
        if (this._isDestroyed) return;

        if (index < fullText.length) {
          // Append chunk
          const chunk = fullText.substr(index, step);
          this.rawText += chunk;

          // Parse and update view
          this.applyParsedText(this.rawText);
          this.scrollContentToBottom();

          index += step;
          this.animationFrameId = requestAnimationFrame(animate);
        } else {
          // Finished
          this.isLoading = false;
          this.rawText = fullText;
          this.applyParsedText(fullText);
          this.scrollContentToBottom();
          this.animationFrameId = null;
        }
      };

      this.animationFrameId = requestAnimationFrame(animate);
    },
    async loadRecentUpdates(forceRefresh = false) {
      if (!this.userId) return;
      if (!forceRefresh && this.cachedText) return;

      this.isLoading = true;
      this.summaryBullets = [];
      this.detailBlocks = [];
      this.rawText = '';

      let buffer = '';
      let accumulatedText = '';

      try {
        await streamMyHCPUpdates({
          data: { content: `user_id=${this.userId}&today=${moment().format('YYYY-MM-DD')}` },
          onChunk: (chunk) => {
            buffer += chunk;
            const lines = buffer.split('\n');
            buffer = lines.pop();

            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed.startsWith('data:')) continue;
              const jsonStr = trimmed.substring(5).trim();
              if (!jsonStr) continue;

              try {
                const json = JSON.parse(jsonStr);
                if (json.output && typeof json.output.text === 'string') {
                  const newText = json.output.text;
                  if (accumulatedText && newText.startsWith(accumulatedText)) {
                    accumulatedText = newText;
                  } else {
                    accumulatedText += newText;
                  }
                  this.rawText = accumulatedText;
                  this.applyParsedText(accumulatedText);
                }
              } catch (e) {
                // Ignore JSON parse errors
              }
            }
          },
          onComplete: () => {
            this.isLoading = false;
            if (accumulatedText) {
              this.rawText = accumulatedText;
              this.applyParsedText(accumulatedText);
              // Save Cache
              if (forceRefresh) {
                saveUserRecentHcpUpdateSummary({
                  user_id: this.userId,
                  role_id: this.roleId,
                  summary: accumulatedText,
                  create_time: moment().format('YYYY-MM-DD HH:mm:ss')
                }).catch(err => console.error('Save cache failed', err));
              }
            }
          },
          onError: () => {
            this.isLoading = false;
          }
        });
      } catch (e) {
        this.isLoading = false;
      }
    },
    handleCustomQuestion() {
      if (!this.customQuestion.trim()) return;
      this.askFollowUp(this.customQuestion);
      this.customQuestion = '';
    },
    askFollowUp(question) {
      const content = `${this.$t('based_on_recent_updates')}${question}`;
      this.$root.$emit('open-ai-chat', content, true);
    }
  }
};
</script>

<style scoped lang="scss">
.recent-updates {
  height: 100%;
  background-color: #fff;
  display: flex;
  flex-direction: column;
}

.header {
  height: 48px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  // box-shadow: 0 1px 2px rgba(0,0,0,0.03); // removed shadow for cleaner look
  border-bottom: 1px solid #f5f5f5;
  flex-shrink: 0;

  .title {
    font-size: 16px;
    font-weight: 600;
    color: #333;
  }

  .left, .right {
    width: 24px;
    display: flex;
    align-items: center;
  }
}

.content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

/* Highlight Box (Summary) */
.highlight-box {
  background: linear-gradient(180deg, #fff5f5 0%, #fff 100%);
  border: 1px solid #ffebeb;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 30px;

  .box-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;

    .box-title {
      font-size: 15px;
      font-weight: bold;
      color: #aa341b;
      display: flex;
      align-items: center;
    }

    .refresh-btn {
      color: #999;
      cursor: pointer;
      padding: 0;
      display: flex;
      align-items: center;
      font-size: 13px;

      span {
        margin-left: 4px;
        position: relative;
        top: 1px;
      }

      &:active { opacity: 0.7; }
    }
  }

  .summary-list {
    .summary-item {
      display: flex;
      align-items: flex-start;
      margin-bottom: 12px;
      line-height: 1.2;

      &:last-child { margin-bottom: 0; }

      .bullet-point {
        width: 6px;
        height: 6px;
        background-color: #aa341b;
        border-radius: 50%;
        margin-top: 9px;
        margin-right: 10px;
        flex-shrink: 0;
        opacity: 0.8;
      }

      .text {
        font-size: 14px;
        color: #333;
        font-weight: 500;
      }
    }
    .empty { color: #999; font-size: 14px; }
  }
}

/* Section Containers */
.section-container {
  margin-bottom: 30px;

  .section-title {
    font-size: 16px;
    font-weight: bold;
    color: #222;
    margin-bottom: 16px;
    display: flex;
    align-items: baseline;

    .subtitle {
      font-size: 12px;
      color: #999;
      font-weight: normal;
      margin-left: 8px;
    }
  }

  .empty-text {
    color: #ccc;
    font-size: 14px;
  }
}

/* Detail Content */
.detail-content {
  padding: 0 8px;
  .detail-block {
    margin-bottom: 16px;
  }

  .detail-text {
    font-size: 14px;
    color: #444;
    line-height: 1.35;
    text-align: left;
    letter-spacing: 0;
    word-spacing: normal;
  }

  .detail-list {
    padding: 0;
    margin: 8px 0;
    list-style: none;

    li {
      display: flex;
      align-items: flex-start;
      margin-bottom: 8px;
      font-size: 14px;
      line-height: 1.35;
      color: #444;
      letter-spacing: 0;
      word-spacing: normal;

      .list-dot {
        width: 4px;
        height: 4px;
        background: #999;
        border-radius: 50%;
        margin-top: 10px;
        margin-right: 8px;
        flex-shrink: 0;
      }
    }
  }
}

.customer-update-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.customer-update-card {
  padding: 14px;
  border: 1px solid #f0e5e2;
  border-radius: 14px;
  background: linear-gradient(145deg, #fff 0%, #fffaf8 100%);
  box-shadow: 0 3px 12px rgba(50, 50, 50, 0.05);
}

.customer-update-head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.customer-update-avatar {
  display: flex;
  flex: 0 0 34px;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  color: #aa341b;
  background: #fcece7;
  font-size: 15px;
  font-weight: 700;
}

.customer-update-identity {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.customer-update-identity strong {
  color: #323233;
  font-size: 15px;
  line-height: 20px;
}

.customer-update-identity span {
  color: #8b8b8b;
  font-size: 11px;
  line-height: 16px;
}

.customer-update-type {
  flex: 0 1 auto;
  max-width: 42%;
  padding: 3px 7px;
  border-radius: 999px;
  color: #9a3c25;
  background: #fbeeea;
  font-size: 10px;
  line-height: 15px;
  text-align: center;
  overflow-wrap: anywhere;
}

.customer-update-summary {
  margin-top: 11px;
  padding: 10px 11px;
  border-radius: 10px;
  color: #4b4b4b;
  background: rgba(247, 247, 248, 0.9);
  font-size: 13px;
  line-height: 1.55;
  text-align: left;
  overflow-wrap: anywhere;
}

.customer-update-meta {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin-top: 9px;
  color: #969799;
  font-size: 11px;
  line-height: 16px;
}

.customer-update-meta .van-icon {
  flex: 0 0 auto;
  margin-top: 2px;
}

/* Follow-up Chips */
.follow-up-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.follow-up-chip {
  background: linear-gradient(#fff, #fff) padding-box, linear-gradient(165deg, #4d50e9 0%, #b84fd7 50%, #aa341b 100%) border-box;
  border: 1.5px solid transparent;
  border-radius: 16px;
  padding: 7px 10px;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 9px;
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 8px 18px rgba(77, 80, 233, 0.08);
  transition: transform 0.18s ease, box-shadow 0.18s ease;

  &:active {
    transform: scale(0.99);
    box-shadow: 0 4px 12px rgba(170, 52, 27, 0.1);
  }
}

.custom-ai-icon {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: linear-gradient(165deg, rgba(77, 80, 233, 0.12) 0%, rgba(184, 79, 215, 0.12) 50%, rgba(170, 52, 27, 0.12) 100%);
  color: #aa341b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.follow-up-label {
  flex: 1;
  min-width: 0;
  line-height: 1.3;
  font-weight: 600;
  background: linear-gradient(165deg, #4d50e9 0%, #b84fd7 50%, #aa341b 100%);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
}

.follow-up-arrow {
  flex: 0 0 auto;
  margin-left: auto;
  color: #b84fd7;
}

/* Custom Input */
.custom-input-wrapper {
  display: flex;
  align-items: center;
  background: linear-gradient(#fff, #fff) padding-box, linear-gradient(165deg, rgba(77, 80, 233, 0.28) 0%, rgba(184, 79, 215, 0.28) 50%, rgba(170, 52, 27, 0.28) 100%) border-box;
  border: 1.5px solid transparent;
  border-radius: 16px;
  padding: 7px 10px;
  margin-top: 8px;
  box-shadow: 0 8px 18px rgba(77, 80, 233, 0.06);
  transition: box-shadow 0.18s ease;

  &:focus-within {
    box-shadow: 0 8px 22px rgba(170, 52, 27, 0.14);
  }

  .custom-question-input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 13px;
    color: #333;
    padding: 0 8px;
    background: transparent;
    min-width: 0;

    &::placeholder {
      color: #9ca3af;
    }
  }

  .send-btn {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: linear-gradient(165deg, #4d50e9 0%, #b84fd7 50%, #aa341b 100%);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
  }
}

/* Animations */
.ai-loading-container {
  display: flex;
  align-items: center;
  padding: 10px 0;
}

.typing-indicator {
  display: inline-flex;
  align-items: center;
  margin-left: 0;
  vertical-align: middle;
  height: 20px;

  .dot {
    width: 6px;
    height: 6px;
    margin: 0 2px;
    background-color: #999;
    border-radius: 50%;
    animation: typing 1.4s infinite ease-in-out both;

    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
  }
}

@keyframes typing {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}

.ai-bubble {
  background: #f2f3f5;
  padding: 10px 14px;
  border-radius: 0 12px 12px 12px;
  color: #333;
  font-size: 13px;
  display: flex;
  align-items: center;
  line-height: 1;
}
</style>
